import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  postSupportCommentByTicketUseCase,
  postSupportCommentByTicketWithFilesUseCase,
} from "@src/cloud-connect/support/use-cases/post-support-comment-by-ticket-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { RootState } from "@src/reducers/store";
import {
  PostSupportCommentByTicketPayload,
  TicketCommentItem,
} from "@src/types/support";
import { getSupportCommentsByTicketWithLanguageUseCase } from "@src/cloud-connect/support/use-cases/get-support-comments-by-ticket-with-language-use-case";
import { UploadedAttachment } from "@src/components/atoms/upload-attachment/upload-attachment";

interface UseTicketPageChatHookProps {
  ticketId: string;
  refreshTrigger: number;
}

export const useTicketPageHookChat = ({
  ticketId,
  refreshTrigger,
}: UseTicketPageChatHookProps) => {
  const userId = useSelector(
    (state: RootState) => state.yourSlice.authContext.userId,
  );

  const [comments, setComments] = useState<TicketCommentItem[]>([]);
  const [commentsContextKey, setCommentsContextKey] = useState<string | null>(
    null,
  );
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<UploadedAttachment | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [attachmentRemoveKey, setAttachmentRemoveKey] = useState(0);
  const previousRefreshTriggerRef = useRef(refreshTrigger);

  const ticketActivationRef = useRef({ ticketId, id: 0 });
  if (ticketActivationRef.current.ticketId !== ticketId) {
    ticketActivationRef.current = {
      ticketId,
      id: ticketActivationRef.current.id + 1,
    };
  }
  const ticketActivationId = ticketActivationRef.current.id;
  const fetchingCommentsForTicketsRef = useRef(
    new Map<string, Promise<void>>(),
  );
  const latestFetchCommentsRef = useRef<(() => Promise<void>) | null>(null);
  const pendingCommentRefreshesRef = useRef(new Set<string>());

  useEffect(() => {
    setComments([]);
    setInput("");
    setIsLoading(true);
    setIsSending(false);
    setIsUploadingAttachment(false);
    setSelectedAttachment(null);
    setSelectedImages([]);
    setAttachmentRemoveKey((previous) => previous + 1);
  }, [ticketActivationId, ticketId]);
  const language = useSelector((state: RootState) => state.yourSlice.language);
  const languageRef = useRef(language);
  languageRef.current = language;
  const commentsRequestKey = `${ticketActivationId}:${language ?? "en"}`;

  const lastEvent = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-support"]?.["lastEvent"],
  );

  const lastEventAt = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-support"]?.["lastEventAt"],
  );

  const events = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-support"]?.["events"] ?? {},
  );

  const isSSEUnavailable = useSelector(
    (state: RootState) =>
      !state.sseSlice.streams?.["service-support"]?.isConnected,
  );

  const fetchComments = useCallback((): Promise<void> => {
    if (!ticketId) return Promise.resolve();

    const currentRequest =
      fetchingCommentsForTicketsRef.current.get(commentsRequestKey);
    if (currentRequest) {
      pendingCommentRefreshesRef.current.add(commentsRequestKey);
      return currentRequest;
    }

    const fetchUntilFresh = async () => {
      let hasShownFetchError = false;
      do {
        pendingCommentRefreshesRef.current.delete(commentsRequestKey);
        const result = await getSupportCommentsByTicketWithLanguageUseCase(
          ticketId,
          language,
        );

        const isCurrentActivation =
          ticketActivationRef.current.ticketId === ticketId &&
          ticketActivationRef.current.id === ticketActivationId &&
          languageRef.current === language;
        if (result.success && result.data) {
          if (isCurrentActivation) {
            setComments(result.data.comments ?? []);
            setCommentsContextKey(commentsRequestKey);
          }
          hasShownFetchError = false;
        } else if (isCurrentActivation && !hasShownFetchError) {
          errorSnackbar(result.error);
          hasShownFetchError = true;
        }
      } while (
        pendingCommentRefreshesRef.current.has(commentsRequestKey) &&
        ticketActivationRef.current.ticketId === ticketId &&
        ticketActivationRef.current.id === ticketActivationId &&
        languageRef.current === language
      );

      pendingCommentRefreshesRef.current.delete(commentsRequestKey);
    };

    const request = fetchUntilFresh().finally(() => {
      pendingCommentRefreshesRef.current.delete(commentsRequestKey);
      fetchingCommentsForTicketsRef.current.delete(commentsRequestKey);
    });
    fetchingCommentsForTicketsRef.current.set(commentsRequestKey, request);
    return request;
  }, [commentsRequestKey, ticketActivationId, ticketId, language]);
  latestFetchCommentsRef.current = fetchComments;

  useEffect(() => {
    setIsLoading(true);
  }, [language]);

  useEffect(() => {
    if (lastEvent !== "comment_created" && lastEvent !== "ticket_updated") {
      return;
    }
    const eventPayload = events?.[lastEvent];
    const eventTicketId = eventPayload?.ticket_id;
    if (eventTicketId === null || eventTicketId !== ticketId) {
      return;
    }
    void latestFetchCommentsRef.current?.();
  }, [events, lastEvent, lastEventAt, ticketId]);

  // Handle new manual refresh triggers (e.g., from auto-translate toggle).
  // Fetch changes caused by language/ticket changes are handled by the initial-fetch effect.
  useEffect(() => {
    if (refreshTrigger === previousRefreshTriggerRef.current) return;
    previousRefreshTriggerRef.current = refreshTrigger;
    void latestFetchCommentsRef.current?.();
  }, [refreshTrigger]);

  //initial fetch of comments when component mounts
  useEffect(() => {
    if (!ticketId) return;
    fetchComments().finally(() => {
      if (
        ticketActivationRef.current.ticketId === ticketId &&
        ticketActivationRef.current.id === ticketActivationId &&
        languageRef.current === language
      ) {
        setIsLoading(false);
      }
    });
  }, [fetchComments, language, ticketActivationId, ticketId]);

  const handleClearAttachment = useCallback(() => {
    setSelectedAttachment(null);
    setAttachmentRemoveKey((prev) => prev + 1);
  }, []);

  const sendMessage = useCallback(
    async (token?: string, type?: string, onAttachmentCleared?: () => void) => {
      const sendActivationId = ticketActivationId;
      const attachmentAtSend = selectedAttachment;
      const inputAtSend = input;
      const content = inputAtSend.trim();
      const imagesAtSend = selectedImages;
      const hasImages = imagesAtSend.length > 0;
      const isAttachment = type === "ATTACHMENT" && Boolean(token);

      if (
        (!content && !isAttachment && !hasImages) ||
        isSending ||
        isSSEUnavailable
      ) {
        return;
      }

      setIsSending(true);

      const payload: PostSupportCommentByTicketPayload =
        isAttachment || hasImages
          ? {
              author_id: userId,
              type: "ATTACHMENT",
              upload_tokens: token ? [token] : [],
              text: content,
            }
          : {
              author_id: userId,
              type: "TEXT",
              text: content,
            };

      try {
        const result = hasImages
          ? await postSupportCommentByTicketWithFilesUseCase(
              ticketId,
              payload,
              imagesAtSend,
            )
          : await postSupportCommentByTicketUseCase(ticketId, payload);

        if (!result.success) {
          if (
            ticketActivationRef.current.ticketId === ticketId &&
            ticketActivationRef.current.id === sendActivationId
          ) {
            errorSnackbar(result.error);
          }
          return;
        }

        if (
          ticketActivationRef.current.ticketId === ticketId &&
          ticketActivationRef.current.id === sendActivationId
        ) {
          setInput((current) => (current === inputAtSend ? "" : current));
          setSelectedImages((current) =>
            current === imagesAtSend ? [] : current,
          );
          if (
            token &&
            attachmentAtSend?.token === token &&
            selectedAttachment === attachmentAtSend
          ) {
            onAttachmentCleared?.();
          }
          await latestFetchCommentsRef.current?.();
        }
      } finally {
        if (
          ticketActivationRef.current.ticketId === ticketId &&
          ticketActivationRef.current.id === sendActivationId
        ) {
          setIsSending(false);
        }
      }
    },
    [
      input,
      isSending,
      isSSEUnavailable,
      ticketActivationId,
      selectedAttachment,
      selectedImages,
      ticketId,
      userId,
    ],
  );

  const handleSendWithAttachment = useCallback(() => {
    if (selectedAttachment) {
      void sendMessage(
        selectedAttachment.token,
        "ATTACHMENT",
        handleClearAttachment,
      );
      return;
    }

    void sendMessage();
  }, [handleClearAttachment, selectedAttachment, sendMessage]);

  const handleAttachmentChange = useCallback(
    (attachment: UploadedAttachment | null) => {
      if (
        ticketActivationRef.current.ticketId === ticketId &&
        ticketActivationRef.current.id === ticketActivationId
      ) {
        setSelectedAttachment(attachment);
      }
    },
    [ticketActivationId, ticketId],
  );

  const handleImagesChange = useCallback((images: File[]) => {
    setSelectedImages(images);
  }, []);

  const handleRemoveImage = useCallback((index: number) => {
    setSelectedImages((previous) => previous.filter((_, i) => i !== index));
  }, []);

  const handleUploadingChange = useCallback(
    (isUploading: boolean) => {
      if (
        ticketActivationRef.current.ticketId === ticketId &&
        ticketActivationRef.current.id === ticketActivationId
      ) {
        setIsUploadingAttachment(isUploading);
      }
    },
    [ticketActivationId, ticketId],
  );

  const displayedComments =
    commentsContextKey === commentsRequestKey ? comments : [];

  const isSendIconDisabled =
    (!input.trim() && !selectedAttachment && selectedImages.length === 0) ||
    isSending ||
    isUploadingAttachment ||
    isSSEUnavailable;

  return {
    comments: displayedComments,
    input,
    isLoading,
    isSending,
    isUploadingAttachment,
    selectedAttachment,
    selectedImages,
    attachmentRemoveKey,
    isSendIconDisabled,
    handleSendWithAttachment,
    handleClearAttachment,
    handleAttachmentChange,
    handleImagesChange,
    handleRemoveImage,
    handleUploadingChange,
    isSSEUnavailable,
    setInput,
  };
};
